import { memo, useCallback, useEffect, useMemo, useState } from 'react';
import { useNavigate, useParams } from 'react-router-dom';
import { assetsChaptersBulkUpdateCreate } from '@goldcast/api/content';
import { isSplitsInTimelineEnabledPure } from '@goldcast/player-config/src/utils/clipUtils';
import { ChapterActionsMenuProps, ChapterType } from './ClipTimelineTypes';
import { getSegmentAtCurrentFrame, getSegmentTimeFromPlayerTime } from '../ClipNewTimeline/timeUtils';
import RowMenuItem from '@/Pages/Sessions/uiComponents/RowMenuItem';
import { useClipsContext } from '@/context/ClipsContext/ClipsContext';
import { useAppContext } from '@/context/AppContext/AppContext';
import { CustomEvents } from '@/libs/eventBus/constants';
import EventBus from '@/libs/eventBus/eventBus';
import { MenuItemsLabelType, menuItems } from '@/Pages/Sessions/AssetsTable/AssetsTableUtils';
import ConfirmationDialog from '@/components/organisms/ConfirmationDialog';
import useDialog from '@/components/organisms/useDialog';
import featureFlagStore from '@/stores/featureFlagStore';
import { FeatureFlagKeys } from '@/services/featureFlag';
import { addPendingUpdate, PendingEditType } from '@/stores/pendingEditsStore';
import { useTranscriptContext } from '@/context/TranscriptContext/TranscriptContext';

function ChapterActionsMenu({
  type,
  chapterStartTime,
  chapterEndTime,
  onCreateClip,
  chapterId,
  onRenameChapter
}: ChapterActionsMenuProps) {
  const { eventId, broadcastId } = useParams<{ eventId: string; broadcastId: string }>();
  const [isClipCreated, setIsClipCreated] = useState(false);
  const [isDeletingChapter, setIsDeletingChapter] = useState(false);
  const { clipData } = useClipsContext();
  const transcriptStore = useTranscriptContext();
  const chapters = useMemo(() => transcriptStore.chapterData, [transcriptStore.chapterData]);
  const { logger } = useAppContext();
  const navigate = useNavigate();

  const {
    isOpen: isRemoveChapterConfirmationOpen,
    openDialog: openRemoveChapterConfirmationDialog,
    closeDialog: closeRemoveChapterConfirmationDialog
  } = useDialog();

  useEffect(() => {
    if (isClipCreated) {
      navigate(`/${eventId}/${broadcastId}/clips`);
    }
  }, [broadcastId, eventId, isClipCreated, navigate]);

  const onCreateClipAction = useCallback(() => {
    setIsClipCreated(false);
    if (!chapterEndTime) {
      logger.error('Chapter end time is undefined. Cannot create clip.');
      return;
    }
    if (
      isSplitsInTimelineEnabledPure({
        clip: clipData,
        isSplitsTranslationEnabled: !!featureFlagStore.getSnapshot()[FeatureFlagKeys.Use_CL_Splits_Translations]
      })
    ) {
      const startSegment = getSegmentAtCurrentFrame(chapterStartTime, clipData);
      const endSegment = getSegmentAtCurrentFrame(chapterEndTime, clipData);
      if (startSegment?.id && endSegment?.id) {
        onCreateClip(
          getSegmentTimeFromPlayerTime(chapterStartTime, clipData, startSegment.id),
          getSegmentTimeFromPlayerTime(chapterEndTime, clipData, endSegment.id),
          startSegment.id,
          endSegment.id
        );
      }
    } else {
      onCreateClip(chapterStartTime, chapterEndTime);
    }
  }, [clipData, chapterStartTime, chapterEndTime, onCreateClip]);

  const onRemoveChapter = useCallback(() => {
    if (!chapterId || !broadcastId || !chapters) {
      return;
    }
    setIsDeletingChapter(true);
    const previousChapters = structuredClone(chapters);
    const updatedChapters = structuredClone(previousChapters);
    const chapterIndex = updatedChapters.findIndex(chapter => chapter.id === chapterId);
    const toRemoveChapter = updatedChapters[chapterIndex];

    if (chapterIndex !== -1 && chapterIndex !== updatedChapters.length - 1 && toRemoveChapter) {
      updatedChapters[chapterIndex + 1].start_time = toRemoveChapter.start_time;
    }

    const nextChapters = updatedChapters.filter(chapter => chapter.id !== chapterId);

    EventBus.dispatch(CustomEvents.UpdateChapters, {
      chapterData: nextChapters,
      chaptersLastUpdatedAt: new Date().toISOString()
    });

    const removeChapterPromise = assetsChaptersBulkUpdateCreate({
      body: nextChapters,
      queryParams: {
        content_id: broadcastId
      }
    });

    if (clipData.id) {
      addPendingUpdate({
        clipId: clipData.id,
        updatePromise: removeChapterPromise,
        version: Date.now(),
        editType: PendingEditType.chapter
      });
    }

    removeChapterPromise
      .catch(err => {
        logger.error('Error while removing chapter', err);
      })
      .finally(() => {
        setIsDeletingChapter(false);
        closeRemoveChapterConfirmationDialog();
      });
  }, [broadcastId, chapterId, chapters, clipData.id, closeRemoveChapterConfirmationDialog, logger]);

  const onAction = useCallback(
    (actionType: MenuItemsLabelType) => {
      switch (actionType) {
        case 'Rename Chapter':
          onRenameChapter();
          break;
        case 'Create Clip':
          onCreateClipAction();
          break;
        case 'Remove Chapter':
          openRemoveChapterConfirmationDialog();
          break;
      }
    },
    [onCreateClipAction, onRenameChapter, openRemoveChapterConfirmationDialog]
  );
  return (
    <>
      <RowMenuItem
        variant={type === ChapterType.TIMELINE ? 'compact' : 'default'}
        content={null}
        menuItems={menuItems['CHAPTER']}
        menuContainerStyle={
          type === ChapterType.TIMELINE ? '!fixed -translate-x-[calc(100%-20px)] -translate-y-[calc(100%+24px)]' : ''
        }
        positionVariant={type === ChapterType.TIMELINE ? '' : 'top-left'}
        onAction={onAction}
        forceShow={false}
      />
      <ConfirmationDialog
        isOpen={isRemoveChapterConfirmationOpen}
        onClose={closeRemoveChapterConfirmationDialog}
        onConfirm={onRemoveChapter}
        confirmLoading={isDeletingChapter}
        title="Remove Chapter"
        content="Are you sure you want to remove this chapter break in the video?"
        confirmLabel="Yes"
        cancelLabel="Cancel"
      />
    </>
  );
}

export default memo(ChapterActionsMenu);
